import React, { useEffect, useRef, useState } from "react";

import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { ReactTransliterate, TriggerKeys } from "react-transliterate";
import { message as toast } from "antd";

import Header from "../../components/Header";
import { whatsappTemplates } from "../../data/templates";
import { selectToggle } from "../../redux/slices/NavigationSlice";
import api from "../../utils/axios";

import "./message.css";

const MAX_MB = 5;

const WhatsappMessage = () => {
  const { t } = useTranslation();
  const lang = useSelector(selectToggle);
  const fileInput = useRef();

  const [image, setImage] = useState(null); // uploaded File
  const [imageUrl, setImageUrl] = useState(""); // hosted URL (from a template)
  const [preview, setPreview] = useState("");
  const [caption, setCaption] = useState("");
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [to, setTo] = useState("");
  // "one" = single number, "loyal" = 3+ orders, "repeat" = 2+ orders, "all" = everyone
  const [audience, setAudience] = useState("one");
  const [loading, setLoading] = useState(false);
  const [numberError, setNumberError] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [campaign, setCampaign] = useState(null); // background broadcast progress
  const pollRef = useRef(null);

  // stop polling if the page unmounts
  useEffect(() => () => clearInterval(pollRef.current), []);

  const startPolling = (id) => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`whatsapp/campaign/${id}`);
        const job = data && data.body;
        if (!job) return;
        setCampaign(job);
        if (job.status === "COMPLETED") {
          clearInterval(pollRef.current);
          toast.success(
            `Sent ${job.sent}/${job.total}` +
              (job.failed ? ` · ${job.failed} failed` : ""),
          );
        }
      } catch (e) {
        /* transient — keep polling */
      }
    }, 2000);
  };

  const pickImage = (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please choose an image file");
      return;
    }
    if (file.size > MAX_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_MB} MB`);
      return;
    }
    setImageError(false);
    setImage(file);
    setImageUrl("");
    setPreview(URL.createObjectURL(file));
  };

  const handleFileChange = (e) => pickImage(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    pickImage(e.dataTransfer.files[0]);
  };

  const removeImage = () => {
    setImage(null);
    setImageUrl("");
    setPreview("");
    if (fileInput.current) fileInput.current.value = "";
  };

  const applyTemplate = (tpl, index) => {
    setActiveTemplate(index);
    setCaption(tpl.text);
    if (tpl.image) {
      setImage(null);
      setImageUrl(tpl.image);
      setPreview(tpl.image);
      setImageError(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const handleSubmit = () => {
    setNumberError(false);
    setImageError(false);

    // image is optional now — need at least an image/URL OR some text
    if (!image && !imageUrl && !caption.trim()) {
      setImageError(true);
      return;
    }
    if (audience === "one" && !to) {
      setNumberError(true);
      return;
    }

    setLoading(true);
    const isBroadcast = audience !== "one";
    const formData = new FormData();
    if (image) {
      formData.append("image", image);
    } else if (imageUrl) {
      formData.append("mediaUrl", imageUrl);
    }
    formData.append("message", caption);
    if (!isBroadcast) formData.append("phone", to);
    // repeat-customer segments carry a minimum order count (orders since Jul 2022);
    // "all" sends to everyone (no threshold)
    if (audience === "loyal") formData.append("minOrders", 3);
    if (audience === "repeat") formData.append("minOrders", 2);

    const url = isBroadcast ? "whatsapp/sendImages" : "whatsapp/sendImage";
    api
      .post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((response) => {
        if (response.status === 200) {
          if (isBroadcast) {
            // background campaign — start the live progress tracker
            const job = response.data && response.data.body;
            if (job && job.id) {
              setCampaign(job);
              startPolling(job.id);
              toast.success(`Campaign started · ${job.total} recipient(s)`);
            }
          } else {
            toast.success(t("common.sendMessage") + " ✓");
          }
          removeImage();
          setCaption("");
          setTo("");
          setActiveTemplate(null);
        } else {
          toast.error(t("common.errorMessage"));
        }
        setLoading(false);
      })
      .catch((error) => {
        toast.error(t("common.errorMessage"));
        setLoading(false);
        console.error(error);
      });
  };

  return (
    <div>
      <Header />

      <div className="tab-container">
        <section className="container-fluid">
          <div className="wa-head">
            <h3>{t("common.whatsappMessage")}</h3>
            <p>
              Pick a template or upload an image, add a caption, and send it to
              one customer or broadcast to a group (loyal, repeat, or all).
            </p>
          </div>

          <div className="wa-grid">
            {/* ---- image ---- */}
            <div className="wa-upload-col">
              <label className="wa-label">Image (optional)</label>
              {preview ? (
                <div className="wa-preview">
                  <img src={preview} alt="selected" />
                  <div className="wa-preview-actions">
                    <button
                      type="button"
                      className="btn btn-gray-outline btn-sm"
                      onClick={() => fileInput.current.click()}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm"
                      onClick={removeImage}
                    >
                      Remove
                    </button>
                  </div>
                  <span className="wa-filename">
                    {image ? image.name : "from template"}
                  </span>
                </div>
              ) : (
                <div
                  className={imageError ? "wa-drop error" : "wa-drop"}
                  onClick={() => fileInput.current.click()}
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleDrop}
                >
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <circle cx="8.5" cy="8.5" r="1.5" />
                    <path d="m21 15-5-5L5 21" />
                  </svg>
                  <p className="wa-drop-title">Click or drop an image here</p>
                  <p className="wa-drop-sub">PNG or JPG · up to {MAX_MB} MB</p>
                </div>
              )}
              {imageError && (
                <p className="form-validation">Add an image or a message</p>
              )}
              <input
                ref={fileInput}
                type="file"
                accept="image/*"
                hidden
                onChange={handleFileChange}
              />
            </div>

            {/* ---- compose ---- */}
            <div className="wa-compose-col">
              <div className="wa-field">
                <label className="wa-label">Send to</label>
                <div className="wa-recipient">
                  <input
                    type="number"
                    value={to}
                    disabled={audience !== "one"}
                    className="form-control"
                    onChange={(e) => setTo(e.target.value)}
                    placeholder="Mobile number"
                  />
                  <select
                    className="form-select wa-audience"
                    value={audience}
                    onChange={(e) => setAudience(e.target.value)}
                  >
                    <option value="one">Single number</option>
                    <option value="loyal">Loyal customers · 3+ orders</option>
                    <option value="repeat">Repeat customers · 2+ orders</option>
                    <option value="all">All customers</option>
                  </select>
                </div>
                {numberError && (
                  <p className="form-validation">{t("common.required")}</p>
                )}
              </div>

              <div className="wa-field">
                <label className="wa-label">Caption</label>
                <ReactTransliterate
                  renderComponent={(props) => <textarea rows={7} {...props} />}
                  value={caption}
                  onChangeText={(text) => setCaption(text)}
                  className="form-control"
                  lang={lang}
                  enabled={lang !== "en"}
                  triggerKeys={[
                    TriggerKeys.KEY_RETURN,
                    TriggerKeys.KEY_ENTER,
                    TriggerKeys.KEY_SPACE,
                    TriggerKeys.KEY_TAB,
                  ]}
                  placeholder="અહીંયા ટાઈપ કરો...."
                />
              </div>

              <button
                type="button"
                className="button-primary wa-send"
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    <span className="m-2">{t("common.loading")}</span>
                  </>
                ) : (
                  <span className="m-2">{t("common.sendMessage")}</span>
                )}
              </button>

              {campaign && (
                <div className="wa-tracker">
                  <div className="wa-tracker-head">
                    <span>
                      {campaign.status === "COMPLETED"
                        ? "Campaign complete"
                        : "Sending in background…"}
                    </span>
                    <span className="wa-tracker-count">
                      {(campaign.sent || 0) + (campaign.failed || 0)} / {campaign.total}
                    </span>
                  </div>
                  <div className="wa-progress">
                    <div
                      className="wa-progress-bar"
                      style={{
                        width:
                          campaign.total
                            ? Math.round(
                                (((campaign.sent || 0) + (campaign.failed || 0)) /
                                  campaign.total) *
                                  100,
                              ) + "%"
                            : "0%",
                      }}
                    />
                  </div>
                  <div className="wa-tracker-meta">
                    <span className="wa-ok">✓ {campaign.sent || 0} sent</span>
                    {campaign.failed > 0 && (
                      <span className="wa-bad">✕ {campaign.failed} failed</span>
                    )}
                    {campaign.status === "COMPLETED" && (
                      <button
                        type="button"
                        className="btn btn-sm wa-tracker-dismiss"
                        onClick={() => setCampaign(null)}
                      >
                        Dismiss
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* ---- templates ---- */}
          <div className="wa-templates">
            <label className="wa-label">Templates</label>
            <div className="wa-template-list">
              {whatsappTemplates.map((tpl, index) => (
                <button
                  type="button"
                  key={index}
                  className={
                    activeTemplate === index
                      ? "wa-template on"
                      : "wa-template"
                  }
                  onClick={() => applyTemplate(tpl, index)}
                >
                  {tpl.image ? (
                    <img className="wa-template-thumb" src={tpl.image} alt="" />
                  ) : (
                    <span className="wa-template-thumb wa-template-noimg">
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16v12H5.2L4 17.2z" />
                        <path d="M8 9h8M8 12h5" />
                      </svg>
                    </span>
                  )}
                  <span className="wa-template-body">
                    <span className="wa-template-name">
                      {tpl.name}
                      {tpl.image && <span className="wa-template-tag">image</span>}
                    </span>
                    <span className="wa-template-snippet">{tpl.text}</span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default WhatsappMessage;
